import EmptyState from "@/components/data-table/components/EmptyState";
import { RangePicker } from "@/components/range-picker/RangePicker";
import { isValidDate } from "@/components/range-picker/utils";
import { Table, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ROLE_CELL_WIDTH,
  SELECT_CELL_WIDTH,
} from "@/modules/projects/components/schedules/constants";
import { getMaxInclusiveDaysAcrossYearsOfRange } from "@/lib/date";
import { useProjectSchedulesContext } from "@/modules/projects/components/schedules/ProjectSchedulesContext";
import { ProjectSchedulesTableBody } from "@/modules/projects/components/schedules/ProjectSchedulesTableBody";
import { ProjectSchedulesTableFooter } from "@/modules/projects/components/schedules/ProjectSchedulesTableFooter";
import { ProjectSchedulesTableWeeklyHeaders } from "@/modules/projects/components/schedules/ProjectSchedulesTableWeeklyHeader";
import { useProjectSchedulesColumns } from "@/modules/projects/hooks/useProjectSchedulesColumns";
import { ReadProjectScheduleVisit } from "@/types/projectSchedule";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  forwardRef,
  memo,
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { DateRange } from "react-day-picker";
import { ScheduleWithVisitMap } from "@/modules/projects/components/schedules/types";
import { useVirtualizer } from "@tanstack/react-virtual";

interface ProjectSchedulesTableProps extends PropsWithChildren {
  onRowMouseEnter?: (rowId: string) => void;
  onRowMouseLeave?: (rowId: string) => void;
}

const ProjectSchedulesTableComponent = forwardRef<
  HTMLDivElement,
  ProjectSchedulesTableProps
>(({ onRowMouseEnter, onRowMouseLeave }, ref) => {
  const {
    data,
    selectedStartDate,
    selectedEndDate,
    projectStartDate,
    projectEndDate,
    setSelectedStartDate,
    setSelectedEndDate,
  } = useProjectSchedulesContext();
  // Pre-computed visit lookup map
  // So we don't need to do it on every cell render
  const dataWithVisitMap = useMemo<ScheduleWithVisitMap[]>(() => {
    return data.map((schedule) => {
      const visitsByDate = new Map<string, ReadProjectScheduleVisit>();
      schedule.visits.forEach((visit) => {
        visitsByDate.set(visit.date, visit);
      });
      return {
        ...schedule,
        visitsByDate,
      };
    });
  }, [data]);

  const columns = useProjectSchedulesColumns();

  const table = useReactTable<ScheduleWithVisitMap>({
    data: dataWithVisitMap,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
  });

  const rows = table.getRowModel().rows;
  const allColumns = table.getVisibleLeafColumns();

  // First 2 columns are fixed (checkbox and role name), rest are virtualizable
  const FIXED_COLUMN_COUNT = 2;
  const virtualizableColumns = allColumns.slice(FIXED_COLUMN_COUNT);

  // Ref for the scroll container - virtualizer must measure scrollLeft from the element that scrolls.
  // We use a wrapper with overflow-x-auto; the Table's inner div doesn't scroll (table does).
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const dateSelectorRowWidth = SELECT_CELL_WIDTH + ROLE_CELL_WIDTH;
  const onRangeChange = useCallback(
    (date: DateRange) => {
      if (date.from && date.to) {
        setSelectedStartDate(date.from);
        setSelectedEndDate(date.to);
      }
    },
    [setSelectedStartDate, setSelectedEndDate],
  );

  // Column virtualizer for horizontal scrolling (only for day columns)
  const columnVirtualizer = useVirtualizer<
    HTMLDivElement,
    HTMLTableCellElement
  >({
    count: virtualizableColumns.length,
    estimateSize: (index) => virtualizableColumns[index].getSize(),
    getScrollElement: () => scrollContainerRef.current,
    horizontal: true,
    overscan: 3,
  });

  const virtualColumns = columnVirtualizer.getVirtualItems();

  // Calculate virtual padding for columns
  let virtualPaddingLeft: number | undefined;
  let virtualPaddingRight: number | undefined;

  if (columnVirtualizer && virtualColumns?.length) {
    virtualPaddingLeft = virtualColumns[0]?.start ?? 0;
    virtualPaddingRight =
      columnVirtualizer.getTotalSize() -
      (virtualColumns[virtualColumns.length - 1]?.end ?? 0);
  }

  const rangePickerDisabled = useMemo(() => {
    if (isValidDate(projectStartDate) && isValidDate(projectEndDate)) {
      return {
        before: projectStartDate,
        after: projectEndDate,
      };
    }
    return true;
  }, [projectStartDate, projectEndDate]);

  const safeProjectStartDate = isValidDate(projectStartDate)
    ? projectStartDate
    : selectedStartDate;
  const safeProjectEndDate = isValidDate(projectEndDate)
    ? projectEndDate
    : selectedEndDate;

  const maxRangeDays = useMemo(
    () =>
      getMaxInclusiveDaysAcrossYearsOfRange(selectedStartDate, selectedEndDate),
    [selectedStartDate, selectedEndDate],
  );

  return (
    <section className="whitespace-nowrap relative">
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto overflow-y-visible w-full"
        data-testid="schedules-scroll-container"
      >
        <div className="min-w-max">
          <Table
            className="grid table-fixed auto-rows-min overflow-visible"
            tableContainerRef={ref}
            data-testid="schedules-table"
          >
            <TableHeader className="sticky top-0 z-30 backdrop-blur-md bg-slate-200/70 h-[46px]">
              <TableRow>
                <TableHead
                  className="sticky left-0 z-20 py-0 px-2 bg-slate-200 border-r border-solid border-white text-center"
                  style={{ width: dateSelectorRowWidth }}
                >
                  <RangePicker
                    selected={{ from: selectedStartDate, to: selectedEndDate }}
                    startMonth={safeProjectStartDate}
                    endMonth={safeProjectEndDate}
                    maxRangeDays={maxRangeDays}
                    disabled={rangePickerDisabled}
                    onChange={onRangeChange}
                    className="w-[224px] truncate"
                  />
                </TableHead>
                <ProjectSchedulesTableWeeklyHeaders
                  startDate={selectedStartDate}
                  endDate={selectedEndDate}
                />
              </TableRow>
            </TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeader key={headerGroup.id} id={headerGroup.id}>
                <TableRow>
                  {/* Render fixed columns (checkbox and role name) */}
                  {headerGroup.headers
                    .slice(0, FIXED_COLUMN_COUNT)
                    .map((header) => {
                      const columnSize = header.column.columnDef.size;
                      const style = {
                        width: `${columnSize}px`,
                        minWidth: `${columnSize}px`,
                        maxWidth: `${columnSize}px`,
                      };
                      return (
                        <TableHead
                          key={header.id}
                          className={
                            header.column.columnDef.meta?.className || ""
                          }
                          style={style}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </TableHead>
                      );
                    })}
                  {/* Virtual padding left */}
                  {virtualPaddingLeft ? (
                    <TableHead
                      style={{ display: "flex", width: virtualPaddingLeft }}
                    />
                  ) : null}
                  {/* Render virtualized day columns */}
                  {virtualColumns.map((virtualColumn) => {
                    const header =
                      headerGroup.headers[
                        FIXED_COLUMN_COUNT + virtualColumn.index
                      ];
                    const columnSize = header.column.columnDef.size;
                    const style = {
                      width: `${columnSize}px`,
                      minWidth: `${columnSize}px`,
                      maxWidth: `${columnSize}px`,
                    };
                    return (
                      <TableHead
                        key={header.id}
                        className={
                          header.column.columnDef.meta?.className || ""
                        }
                        style={style}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </TableHead>
                    );
                  })}
                  {/* Virtual padding right */}
                  {virtualPaddingRight ? (
                    <TableHead
                      style={{ display: "flex", width: virtualPaddingRight }}
                    />
                  ) : null}
                </TableRow>
              </TableHeader>
            ))}
            <ProjectSchedulesTableBody
              table={table}
              onRowMouseEnter={onRowMouseEnter}
              onRowMouseLeave={onRowMouseLeave}
              columnVirtualizer={columnVirtualizer}
              virtualPaddingLeft={virtualPaddingLeft}
              virtualPaddingRight={virtualPaddingRight}
              fixedColumnCount={FIXED_COLUMN_COUNT}
            />
          </Table>
        </div>
      </div>
      {rows.length === 0 && <EmptyState />}
      <ProjectSchedulesTableFooter />
    </section>
  );
});

export const ProjectSchedulesTable = memo(ProjectSchedulesTableComponent);
